import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { ref, onMounted, onUnmounted, watch } from 'vue';
import { Network, DataSet } from 'vis-network/standalone';
import { LATENCY } from '@/interfaces/SmartLink/Topology';
import { getDeviceModel } from '@/utils/SmartLink/device';


export default /*@__PURE__*/_defineComponent({
  __name: 'CircularTopology',
  props: {
    items: {},
    getOperabilityPercentages: { type: Function },
    formatShortDate: { type: Function }
  },
  emits: ['select-device'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const wrapperRef = ref<HTMLElement | null>(null);
const networkContainer = ref<HTMLElement | null>(null);
const isFullscreen = ref(false);
let networkInstance: Network | null = null;

const zoneDefinitions = [
    { id: 1, name: 'Zona OK', range: '0 - 100 ms', strokeColor: '#00E676', fillColor: 'rgba(0, 230, 118, 0.03)' },
    { id: 2, name: 'Zona Moderada', range: '101 - 500 ms', strokeColor: '#FFEA00', fillColor: 'rgba(255, 234, 0, 0.03)' },
    { id: 3, name: 'Zona Crítica', range: '> 500 ms', strokeColor: '#FF3D00', fillColor: 'rgba(255, 61, 0, 0.03)' },
    { id: 4, name: 'Zona Desconectada', range: 'OFF (-1)', strokeColor: '#9E9E9E', fillColor: 'rgba(158, 158, 158, 0.03)' }
];

const calculatedZoneRadii = ref<{ [key: number]: { inner: number; outer: number } }>({});

function getZoneByLatency(latencia: number) {
    if (latencia < 0) return zoneDefinitions[3];
    if (latencia <= 100) return zoneDefinitions[0];
    if (latencia <= 500) return zoneDefinitions[1];
    return zoneDefinitions[2];
}

function renderTopology() {
    if (!networkContainer.value) return;

    const nodesByZone: { [key: number]: LATENCY[] } = { 1: [], 2: [], 3: [], 4: [] };
    props.items.forEach(item => nodesByZone[getZoneByLatency(item.latencia).id].push(item));

    const nodesData: any[] = [];
    let currentRadiusOffset = 140;
    calculatedZoneRadii.value = {};

    zoneDefinitions.forEach(zone => {
        const items = nodesByZone[zone.id];
        const innerRadius = currentRadiusOffset;

        if (items.length === 0) {
            currentRadiusOffset += 140;
            calculatedZoneRadii.value[zone.id] = { inner: innerRadius, outer: currentRadiusOffset };
            return;
        }

        let orbitRadius = innerRadius + 50;
        let itemsPlaced = 0;

        while (itemsPlaced < items.length) {
            const capacity = Math.max(Math.floor((2 * Math.PI * orbitRadius) / 160), 4);
            const batchSize = Math.min(capacity, items.length - itemsPlaced);

            for (let i = 0; i < batchSize; i++) {
                const item = items[itemsPlaced + i];
                const angle = ((2 * Math.PI) / batchSize) * i + (itemsPlaced / capacity) * 0.5;

                const displayLatency = item.latencia < 0 ? 'OFF' : `${item.latencia}ms`;
                const dateText = props.formatShortDate(item.fecha);
                const modelText = getDeviceModel(item.anotacion);
                const op = props.getOperabilityPercentages(item.ip);

                const svgString = `
                <svg xmlns="http://www.w3.org/2000/svg" width="180" height="105">
                    <rect x="0" y="0" width="180" height="105" rx="8" ry="8" fill="#0F172A" stroke="${zone.strokeColor}" stroke-width="2"/>
                    <text x="90" y="18" fill="#FFFFFF" font-size="11" font-weight="bold" font-family="monospace" text-anchor="middle">${item.name}</text>
                    <text x="90" y="32" fill="#38BDF8" font-size="9" font-family="sans-serif" text-anchor="middle">${modelText}</text>
                    <text x="90" y="47" fill="#00E676" font-size="10" font-family="monospace" text-anchor="middle">${item.ip}</text>
                    <text x="90" y="61" fill="#94A3B8" font-size="9" font-family="monospace" text-anchor="middle">(${displayLatency})</text>
                    <text x="90" y="75" fill="#64748B" font-size="8" font-family="monospace" text-anchor="middle">Upd: ${dateText}</text>
                    <g transform="translate(10, 85)">
                        <rect x="0" y="0" width="160" height="5" rx="2" fill="rgba(255,255,255,0.1)"/>
                        <rect x="0" y="0" width="${(op.ok / 100) * 160}" height="5" rx="2" fill="#00E676"/>
                        <rect x="${(op.ok / 100) * 160}" y="0" width="${(op.alert / 100) * 160}" height="5" fill="#FFEA00"/>
                        <rect x="${((op.ok + op.alert) / 100) * 160}" y="0" width="${(op.alarm / 100) * 160}" height="5" fill="#FF3D00"/>
                        <rect x="${((op.ok + op.alert + op.alarm) / 100) * 160}" y="0" width="${(op.down / 100) * 160}" height="5" rx="2" fill="#9E9E9E"/>
                    </g>
                </svg>`;

                nodesData.push({
                    id: item.ip,
                    shape: 'image',
                    image: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString),
                    size: 50,
                    x: orbitRadius * Math.cos(angle),
                    y: orbitRadius * Math.sin(angle)
                });
            }
            itemsPlaced += batchSize;
            if (itemsPlaced < items.length) orbitRadius += 100;
        }
        currentRadiusOffset = orbitRadius + 110;
        calculatedZoneRadii.value[zone.id] = { inner: innerRadius, outer: currentRadiusOffset };
    });

    if (networkInstance) networkInstance.destroy();
    
    networkInstance = new Network(networkContainer.value, { nodes: new DataSet(nodesData), edges: new DataSet([]) }, { physics: false });

    // RESTAURADO: Dibujo de las zonas concéntricas en el Canvas
    networkInstance.on('beforeDrawing', (ctx: CanvasRenderingContext2D) => {
        zoneDefinitions.forEach(zone => {
            const radii = calculatedZoneRadii.value[zone.id];
            if (!radii) return;

            const outerR = radii.outer;
            ctx.beginPath();
            ctx.arc(0, 0, outerR, 0, 2 * Math.PI, false);
            ctx.fillStyle = zone.fillColor;
            ctx.fill();
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = zone.strokeColor;
            ctx.setLineDash([8, 6]);
            ctx.stroke();
            ctx.setLineDash([]);

            ctx.font = 'bold 12px monospace';
            ctx.fillStyle = zone.strokeColor;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            ctx.fillText(`${zone.name.toUpperCase()} (${zone.range})`, 0, -outerR + 18);
        });
    });

    networkInstance.once('afterDrawing', () => {
        networkInstance?.fit({ animation: { duration: 500, easingFunction: 'easeInOutQuad' } });
    });

    networkInstance.on('click', (params) => {
        if (params.nodes.length > 0) {
            const found = props.items.find(i => i.ip === params.nodes[0]);
            if (found) emit('select-device', found);
        }
    });
}

function toggleFullscreen() {
    if (!wrapperRef.value) return;
    if (!document.fullscreenElement) {
        wrapperRef.value.requestFullscreen();
        isFullscreen.value = true;
    } else {
        document.exitFullscreen();
        isFullscreen.value = false;
    }
}

watch(() => props.items, renderTopology, { deep: true });
onMounted(renderTopology);
onUnmounted(() => networkInstance?.destroy());

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!

  return (_openBlock(), _createElementBlock("div", {
    ref_key: "wrapperRef",
    ref: wrapperRef,
    class: "topology-wrapper"
  }, [
    _createElementVNode("div", {
      ref_key: "networkContainer",
      ref: networkContainer,
      class: "network-container"
    }, null, 512),
    _createVNode(_component_v_btn, {
      icon: "",
      size: "small",
      color: "slate-800",
      class: "fullscreen-btn",
      onClick: toggleFullscreen
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_icon, null, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(isFullscreen.value ? 'mdi-fullscreen-exit' : 'mdi-fullscreen'), 1)
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ], 512))
}
}

})